import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { WorkflowTuningApproach, WorkflowTuningPreview } from '../../../types/workflowTuning';
import { OPTIMIZATION_APPROACH_LABELS } from '../../../types/optimizationApproach';
import {
  buildEvaluatorParamsPayload,
  CONFIG_TUNING_EVALUATION_MODES,
  createDefaultEvaluatorParamsForm,
  EVALUATION_MODE_LABELS,
  evaluationModeHasEvaluatorParams,
  mergeEvaluatorParamsFormForMode,
  normalizeConfigTuningEvaluationMode,
  validateEvaluatorParamsForm,
  type EvaluationMode,
  type EvaluatorParamsFormState,
} from '../../../types/evaluation';
import EvaluatorParamsFields from '../../EvaluatorParamsFields';
import '../../EvaluatorParamsFields.css';
import { autoTune } from '../../../services/api';
import { getErrorMessage } from '../../../utils/errorUtils';
import { formatPredictedCostUsd } from '../../../utils/previewCost';
import { buildWorkflowTuningRequest, suggestStateIds, validateStateIds, type WorkflowTuningForm } from '../../../utils/workflowTuningSpec';
import { Banner, Field, FieldGroup, Pill, Section, SegmentButton, SegmentGroup } from './WorkflowFormControls';

export interface WorkflowTuningLaunchProps {
  /** For the cost preview request — the real run is created by the parent, this page only estimates it. */
  integrationId: string;
  /** Every state in the graph — `state_ids` is checked against this, since nothing downstream does. */
  graphStateIds: string[];
  datasets: string[];
  datasetsLoading?: boolean;
  submitting: boolean;
  onSubmit: (form: Omit<WorkflowTuningForm, 'integrationId'>) => void;
  /** Дообучение моделей агентов — соседний путь оптимизации той же интеграции.
   *  Стоит в одном ряду с режимами, но режимом не является: открывает окно, а не
   *  переключает то, что запустится кнопкой ниже. */
  onOpenFineTuning?: () => void;
  onSubmitEvaluation: (form: {
    datasetNames: string[];
    batchSize?: number;
    evaluationMode?: string;
    datasetSplit?: string;
    evaluatorParams?: Record<string, unknown>;
  }) => void;
  /** Integration models for the LLM-judge picker, as in config tuning. */
  availableModels?: string[];
}

/**
 * Три подхода, а не четыре: MUTATIONS есть в Config Tuning, но в контракте
 * workflow-тюнинга его нет и замеров по нему тоже — отправлять его наугад нельзя.
 */
const APPROACHES: WorkflowTuningApproach[] = ['BASELINE', 'PACE', 'MULTISTAGE-PACE'];

function formatTokens(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}

/** Debounce for the cost preview — a keystroke shouldn't fire a request per character. */
const PREVIEW_DEBOUNCE_MS = 500;

const WorkflowTuningLaunch: React.FC<WorkflowTuningLaunchProps> = ({
  integrationId,
  graphStateIds,
  datasets,
  datasetsLoading,
  submitting,
  onSubmit,
  onSubmitEvaluation,
  onOpenFineTuning,
  availableModels,
}) => {
  const [mode, setMode] = useState<'optimize' | 'evaluate'>('optimize');
  const [datasetNames, setDatasetNames] = useState<string[]>([]);
  const [approach, setApproach] = useState<WorkflowTuningApproach>('BASELINE');
  const [evaluationMode, setEvaluationMode] = useState<EvaluationMode>('bert_score');
  const [evaluatorParams, setEvaluatorParams] = useState<EvaluatorParamsFormState>(() =>
    createDefaultEvaluatorParamsForm('bert_score')
  );
  const [numSteps, setNumSteps] = useState(1);
  const [batchSize, setBatchSize] = useState(2);
  const [validationBatches, setValidationBatches] = useState(1);
  const [nPrompts, setNPrompts] = useState<number | ''>('');
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState('');
  const [parallelIterations, setParallelIterations] = useState(1);
  const [allowFormatRequirements, setAllowFormatRequirements] = useState(false);
  const [readOnlyContext, setReadOnlyContext] = useState(false);
  const [includeOntology, setIncludeOntology] = useState(false);
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(false);
  const [containerGrowthHeadroom, setContainerGrowthHeadroom] = useState(0);
  const [maxStates, setMaxStates] = useState<number | ''>('');
  const [maxRoles, setMaxRoles] = useState<number | ''>('');

  const scopeIsWholeGraph = selectedStates.length === 0;
  const validation = useMemo(() => validateStateIds(selectedStates, graphStateIds), [selectedStates, graphStateIds]);
  const filteredStates = useMemo(() => {
    const q = stateFilter.trim().toLowerCase();
    return q ? graphStateIds.filter((id) => id.toLowerCase().includes(q)) : graphStateIds;
  }, [graphStateIds, stateFilter]);

  // Тело — то же, что уйдёт на запуск джобы (см. submit ниже): превью считается ровно
  // по тем параметрам, с которыми прогон стартует. `null`, пока запускать нечего —
  // без датасета или с неизвестным state_id бэкенд всё равно ответит 400.
  // Один построитель на превью и на запуск: превью обязано считаться ровно по тому
  // телу, которое уйдёт в прогон. Две отдельные сборки разъезжались бы молча —
  // добавили поле в запуск, забыли в превью, и оценка считается не про то.
  const buildOptimizeForm = (): Omit<WorkflowTuningForm, 'integrationId'> => ({
    datasetNames,
    approach,
    evaluationMode,
    // Same evaluator family as config tuning, so the payload is built the same way,
    // direction included — tuning does pick a candidate.
    evaluatorParams: buildEvaluatorParamsPayload(evaluationMode, evaluatorParams, { includeDirection: true }),
    numSteps,
    // Отправляем то, что выбрано: раз запуск с неизвестным id заблокирован,
    // подменять состав на `known` больше незачем — это скрывало бы расхождение.
    stateIds: validation.known,
    parallelIterations,
    allowFormatRequirements,
    readOnlyContext,
    includeOntology,
    includeKnowledgeBase,
    containerGrowthHeadroom,
    limits: {
      maxStates: maxStates === '' ? null : maxStates,
      maxRoles: maxRoles === '' ? null : maxRoles,
    },
    trainConfig: {
      batch_size: batchSize,
      validation_batches: validationBatches,
      ...(approach === 'MULTISTAGE-PACE' && nPrompts !== '' ? { n_prompts_to_generate: nPrompts } : {}),
    },
  });

  const previewForm = useMemo<Omit<WorkflowTuningForm, 'integrationId'> | null>(() => {
    if (mode !== 'optimize' || datasetNames.length === 0 || validation.unknown.length > 0) return null;
    return buildOptimizeForm();
  }, [
    mode,
    datasetNames,
    validation.unknown.length,
    validation.known,
    approach,
    evaluationMode,
    evaluatorParams,
    numSteps,
    parallelIterations,
    allowFormatRequirements,
    readOnlyContext,
    includeOntology,
    includeKnowledgeBase,
    containerGrowthHeadroom,
    maxStates,
    maxRoles,
    batchSize,
    validationBatches,
    nPrompts,
  ]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<WorkflowTuningPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Счётчик, а не AbortController: гонки гасим по свежести ответа — устаревший просто
  // не применяется к состоянию.
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = (previewRequestIdRef.current += 1);
    if (!previewForm) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      autoTune
        .previewWorkflowTuningJob(buildWorkflowTuningRequest({ ...previewForm, integrationId }))
        .then((res) => {
          if (previewRequestIdRef.current !== requestId) return; // ответ на устаревший запрос
          if (res.success && res.data) {
            setPreview(res.data);
            setPreviewError(null);
          } else {
            setPreview(null);
            setPreviewError(res.error?.message || 'Estimate unavailable');
          }
        })
        .catch((e) => {
          if (previewRequestIdRef.current !== requestId) return;
          setPreview(null);
          setPreviewError(getErrorMessage(e, 'Estimate unavailable'));
        })
        .finally(() => {
          if (previewRequestIdRef.current !== requestId) return;
          setPreviewLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [previewForm, integrationId]);

  // A judge with no prompt is a run that burns an hour and returns nothing usable, so
  // the mode's own requirements are checked before the button is live.
  const paramsError = useMemo(
    () => validateEvaluatorParamsForm(evaluationMode, evaluatorParams),
    [evaluationMode, evaluatorParams]
  );
  // Неизвестный id больше не отбрасывается на бэкенде (AIT-294): запуск с ним
  // вернёт 400. Поэтому блокируем на любом неизвестном, а не только когда
  // неизвестны все, — иначе форма молча сужала охват до известных.
  const canSubmit = datasetNames.length > 0 && !submitting && validation.unknown.length === 0 && !paramsError;

  const toggleState = (id: string) =>
    setSelectedStates((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () => {
    if (!canSubmit) return;
    if (mode === 'evaluate') {
      onSubmitEvaluation({
        datasetNames,
        batchSize,
        evaluationMode,
        // No direction here: it steers candidate selection, and a plain evaluation
        // selects nothing.
        evaluatorParams: buildEvaluatorParamsPayload(evaluationMode, evaluatorParams),
      });
      return;
    }
    onSubmit(buildOptimizeForm());
  };

  return (
    <div className="cwt-launch">
      <div className="cwt-launch__modes">
        <button
          type="button"
          className={`cwt-mode ${mode === 'optimize' ? 'cwt-mode--active' : ''}`}
          onClick={() => setMode('optimize')}
        >
          <span className="cwt-mode__icon"><i className="fas fa-wand-magic-sparkles" aria-hidden="true" /></span>
          <span><strong>Optimize</strong><small>Improve prompts and graph behavior</small></span>
        </button>
        <button
          type="button"
          className={`cwt-mode ${mode === 'evaluate' ? 'cwt-mode--active' : ''}`}
          onClick={() => setMode('evaluate')}
        >
          <span className="cwt-mode__icon"><i className="fas fa-ruler" aria-hidden="true" /></span>
          <span><strong>Evaluate only</strong><small>Measure quality without changes</small></span>
        </button>
        {onOpenFineTuning ? (
          <button
            type="button"
            className="cwt-mode cwt-mode--action"
            onClick={onOpenFineTuning}
            title="Start Fine Tuning Job for agents marked as trainable"
          >
            <span className="cwt-mode__icon"><i className="fas fa-graduation-cap" aria-hidden="true" /></span>
            <span><strong>Fine-tuning</strong><small>Train agent models on collected data</small></span>
            <i className="fas fa-arrow-up-right-from-square cwt-mode__out" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <p className="cwf-note">
        {mode === 'optimize'
          ? 'Improves the graph one state per internal iteration, then the global sections. The result is a proposal — nothing is written until you apply it.'
          : 'Measures the graph as it stands, without changing anything.'}
      </p>

      <FieldGroup label="Datasets" hint={datasetsLoading ? 'Loading…' : 'Names or ids; at least one is required.'}>
        <div className="cwt-chips">
          {datasets.length === 0 ? (
            <span className="cwf-note">No datasets for this integration.</span>
          ) : (
            datasets.map((name) => (
              <button
                key={name}
                type="button"
                className={`cwt-chip ${datasetNames.includes(name) ? 'cwt-chip--on' : ''}`}
                onClick={() =>
                  setDatasetNames((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]))
                }
              >
                <i className={`fas ${datasetNames.includes(name) ? 'fa-check-circle' : 'fa-database'}`} aria-hidden="true" />
                <span>{name}</span>
              </button>
            ))
          )}
        </div>
      </FieldGroup>

      <div className="cwf-grid-2 cwt-launch__settings">
        {mode === 'optimize' ? (
          <SegmentGroup label="Approach" hint="Cost per step differs a lot between them — see the estimate below.">
            {APPROACHES.map((a) => (
              <SegmentButton key={a} active={approach === a} title={a} onClick={() => setApproach(a)}>
                {OPTIMIZATION_APPROACH_LABELS[a]}
              </SegmentButton>
            ))}
          </SegmentGroup>
        ) : null}
        <Field label="Evaluation mode">
          <select
            className="cwf-input"
            value={evaluationMode}
            onChange={(e) => {
              const next = normalizeConfigTuningEvaluationMode(e.target.value);
              setEvaluationMode(next);
              // Keeps whatever still applies to the new mode and defaults the rest.
              setEvaluatorParams((prev) => mergeEvaluatorParamsFormForMode(next, prev));
            }}
          >
            {CONFIG_TUNING_EVALUATION_MODES.map((m) => (
              <option key={m} value={m}>
                {EVALUATION_MODE_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {evaluationModeHasEvaluatorParams(evaluationMode) ? (
        <Section
          title="Evaluator parameters"
          defaultOpen
          summary={EVALUATION_MODE_LABELS[evaluationMode]}
          badge={paramsError ? <Pill tone="warn">needs a value</Pill> : undefined}
        >
          <p className="cwf-note">
            These depend on the evaluation mode, exactly as in config tuning. Direction applies to candidate
            selection and is sent only when optimizing.
          </p>
          <EvaluatorParamsFields
            idPrefix="workflow-tuning-evaluator"
            mode={evaluationMode}
            value={evaluatorParams}
            onChange={setEvaluatorParams}
            disabled={submitting}
            includeDirection={mode === 'optimize'}
            availableModels={availableModels}
          />
          {paramsError ? (
            <Banner tone="warn" title="The evaluator is not fully configured">
              <p>{paramsError}</p>
            </Banner>
          ) : null}
        </Section>
      ) : null}

      {mode === 'optimize' ? (
        <>
          <div className="cwf-grid-3 cwt-launch__settings cwt-launch__settings--compact">
            <Field label="Steps" hint="Optimization steps, not internal iterations.">
              <input
                type="number"
                min={1}
                className="cwf-input"
                value={numSteps}
                onChange={(e) => setNumSteps(Math.max(1, Number(e.target.value) || 1))}
              />
            </Field>
            <Field label="batch_size">
              <input type="number" min={1} className="cwf-input" value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value) || 0)} />
            </Field>
            <Field label="validation_batches">
              <input
                type="number"
                min={0}
                className="cwf-input"
                value={validationBatches}
                onChange={(e) => setValidationBatches(Number(e.target.value) || 0)}
              />
            </Field>
          </div>
          {approach === 'MULTISTAGE-PACE' ? (
            <Field label="n_prompts_to_generate" hint="Each variant runs every slice — two variants cost twice as much.">
              <input
                type="number"
                min={1}
                className="cwf-input"
                value={nPrompts}
                onChange={(e) => setNPrompts(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Field>
          ) : null}

          <Section
            title="Scope"
            defaultOpen
            summary={scopeIsWholeGraph ? `whole graph · ${graphStateIds.length} states` : `${validation.known.length} states`}
          >
            <p className="cwf-note">
              Nothing selected means the whole graph. Narrowing the scope is the main lever on cost: the contract's
              single-state run took 2 iterations instead of 29.
            </p>
            <div className="cwt-scope__toolbar">
              <input
                className="cwf-input cwf-input--sm"
                placeholder={`Filter ${graphStateIds.length} states…`}
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              />
              <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => setSelectedStates([])}>
                Whole graph
              </button>
            </div>
            <div className="cwt-scope__list">
              {filteredStates.map((id) => (
                <label key={id} className="cwf-check cwf-check--sm cwt-scope__item">
                  <input type="checkbox" checked={selectedStates.includes(id)} onChange={() => toggleState(id)} />
                  <span>{id}</span>
                </label>
              ))}
              {filteredStates.length === 0 ? <span className="cwf-note">No states match the filter.</span> : null}
            </div>
            {validation.unknown.length > 0 ? (
              <Banner
                tone="error"
                title="These ids are not in the graph"
                details={validation.unknown.map((id) => {
                  const near = suggestStateIds(id, graphStateIds);
                  return near.length ? `${id} — did you mean ${near.join(', ')}?` : id;
                })}
              >
                <p>
                  The run is rejected with <code>400 workflow_state_unknown</code>, so the launch stays blocked until every
                  id is one the graph has. Refresh the graph if a state was renamed.
                </p>
              </Banner>
            ) : null}
          </Section>

          <Section title="Advanced" summary={parallelIterations > 1 ? `parallel ×${parallelIterations}` : 'defaults'}>
            <div className="cwf-grid-2">
              <Field
                label="parallel_iterations"
                hint="Above 1 is a different mode: parallel slices all see the pre-step graph, so states can drift apart. A draft pass, not a result to keep."
              >
                <input
                  type="number"
                  min={1}
                  className="cwf-input"
                  value={parallelIterations}
                  onChange={(e) => setParallelIterations(Math.max(1, Number(e.target.value) || 1))}
                />
              </Field>
              <Field
                label="container_growth_headroom"
                hint="Above 0 rewrites a state's workers as a whole list instead of per field — mutually exclusive with per-field edits."
              >
                <input
                  type="number"
                  min={0}
                  className="cwf-input"
                  value={containerGrowthHeadroom}
                  onChange={(e) => setContainerGrowthHeadroom(Math.max(0, Number(e.target.value) || 0))}
                />
              </Field>
            </div>
            <div className="cwf-grid-2">
              <Field label="max_states" hint="Empty = no limit. The limiter counts entities, not characters.">
                <input
                  type="number"
                  min={1}
                  className="cwf-input"
                  value={maxStates}
                  onChange={(e) => setMaxStates(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Field>
              <Field label="max_roles" hint="Empty = no limit.">
                <input
                  type="number"
                  min={1}
                  className="cwf-input"
                  value={maxRoles}
                  onChange={(e) => setMaxRoles(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Field>
            </div>
            <label className="cwf-check cwf-check--sm">
              <input type="checkbox" checked={allowFormatRequirements} onChange={(e) => setAllowFormatRequirements(e.target.checked)} />
              <span>allow_format_requirements — unfreeze format_requirements</span>
            </label>
            <label className="cwf-check cwf-check--sm">
              <input type="checkbox" checked={readOnlyContext} onChange={(e) => setReadOnlyContext(e.target.checked)} />
              <span>read_only_context</span>
            </label>
            <label className="cwf-check cwf-check--sm">
              <input type="checkbox" checked={includeOntology} onChange={(e) => setIncludeOntology(e.target.checked)} />
              <span>include_ontology</span>
            </label>
            <label className="cwf-check cwf-check--sm">
              <input type="checkbox" checked={includeKnowledgeBase} onChange={(e) => setIncludeKnowledgeBase(e.target.checked)} />
              <span>include_knowledge_base</span>
            </label>
          </Section>
        </>
      ) : (
        <Field label="batch_size">
          <input type="number" min={1} className="cwf-input" value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value) || 0)} />
        </Field>
      )}

      {mode === 'optimize' ? (
        <aside className="cwt-estimate">
          <span className="cwt-estimate__icon"><i className="fas fa-gauge-high" aria-hidden="true" /></span>
          <div className="cwt-estimate__copy">
            <span className="cwt-estimate__eyebrow">Estimated workload</span>
            {previewLoading ? (
              <strong><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Recalculating…</strong>
            ) : previewError ? (
              <>
                <strong>Estimate unavailable</strong>
                <p>{previewError} — this does not block starting the run.</p>
              </>
            ) : preview ? (
              <>
                <strong>
                  {formatTokens(preview.expected_total_tokens)} tokens · {formatPredictedCostUsd(preview.predicted_total_cost_usd)}
                </strong>
                {preview.breakdown?.components && preview.breakdown.components.length > 0 ? (
                  <ul className="cwt-estimate__components">
                    {preview.breakdown.components.map((c) => (
                      <li key={c.label}>
                        <span className="cwt-estimate__component-label">{c.label}</span>
                        <span>{formatTokens((c.input_tokens ?? 0) + (c.output_tokens ?? 0))}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {(() => {
                  const u = preview.breakdown?.unit_counts;
                  if (!u) return null;
                  const parts = [
                    u.states != null ? `${u.states} states` : null,
                    u.steps != null ? `${u.steps} steps` : null,
                    u.evaluations != null ? `${u.evaluations} evaluations` : null,
                    u.turns != null ? `${u.turns} turns` : null,
                  ].filter((v): v is string => Boolean(v));
                  return parts.length > 0 ? <p className="cwt-estimate__units">{parts.join(' · ')}</p> : null;
                })()}
                {preview.warnings && preview.warnings.length > 0 ? (
                  <p className="cwt-estimate__warnings">
                    <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {preview.warnings.join(' ')}
                  </p>
                ) : null}
              </>
            ) : (
              <p>Pick a dataset and a valid scope to see the estimate.</p>
            )}
          </div>
          <span className="cwt-estimate__scope">{scopeIsWholeGraph ? 'Full graph' : `${validation.known.length} states`}</span>
        </aside>
      ) : null}

      <div className="cwt-launch__actions">
        <span className="cwt-launch__ready">
          {validation.allUnknown ? <Pill tone="danger">every requested id is unknown</Pill> : datasetNames.length ? <><i className="fas fa-circle-check" aria-hidden="true" /> {datasetNames.length} dataset{datasetNames.length === 1 ? '' : 's'} ready</> : <><i className="fas fa-circle-info" aria-hidden="true" /> Select a dataset to continue</>}
        </span>
        <button type="button" className="cwf-btn cwf-btn--primary" disabled={!canSubmit} onClick={submit}>
          {submitting ? (
            <>
              <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Starting…
            </>
          ) : (
            <>
              <i className="fas fa-play" aria-hidden="true" /> {mode === 'optimize' ? 'Start tuning' : 'Start evaluation'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WorkflowTuningLaunch;
